import type { FC } from 'react';

import type {
  LoggedInTransferForm,
  TransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import {
  useLoggedInTransferFormData,
  useLoggedInTransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';

interface TransferFormProps {
  loggedInTransferFormData: LoggedInTransferForm;
  setLoggedInTransferFormData: (data: LoggedInTransferForm) => void;
  loggedInTransferFormProgress: TransferFormProgress;
  setLoggedInTransferFormProgress: (progress: TransferFormProgress) => void;
}
/*
 * Provides a HOC that injects the logged in transfer form data and progress into a wrapped component
 *
 * I did this to be able to set this data within the Family Member Add container, where it was not
 * feasible to fully disentangle redux at the moment.
 *
 * @param WrappedComponent - The component to wrap
 * @returns A new component with the logged in transfer form data and progress as props
 */
export const withLoggedInTransferForm = <P extends TransferFormProps>(WrappedComponent: FC<P>) => {
  const WithLoggedInTransferForm = (props: Omit<P, keyof TransferFormProps>) => {
    const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();
    const { loggedInTransferFormProgress, setLoggedInTransferFormProgress } = useLoggedInTransferFormProgress();

    return (
      <WrappedComponent
        {...(props as P)}
        loggedInTransferFormData={loggedInTransferFormData}
        setLoggedInTransferFormData={setLoggedInTransferFormData}
        loggedInTransferFormProgress={loggedInTransferFormProgress}
        setLoggedInTransferFormProgress={setLoggedInTransferFormProgress}
      />
    );
  };

  return WithLoggedInTransferForm;
};
